import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ComplianceStatusResponse } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { Button } from '@fetmode/ui';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/index.js';

export function VerifyPage(): ReactElement {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ComplianceStatusResponse | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState('2000-01-01');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadStatus = useCallback(async () => {
    const result = await api.getComplianceStatus();
    setStatus(result);
    if (result.canAccess) {
      void navigate('/wallet', { replace: true });
    }
  }, [api, navigate]);

  useEffect(() => {
    void loadStatus()
      .catch(() => {
        setError('Unable to load verification requirements.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [loadStatus]);

  const handleAttest = async (): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (!acceptedTerms) {
        setError('You must accept the terms.');
        return;
      }

      await api.submitAgeAttestation({
        dateOfBirth,
        acceptedTerms: true,
      });
      await loadStatus();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Attestation failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartVerification = async (): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const session = await api.startAgeVerification();
      if (session.alreadyVerified) {
        await loadStatus();
        void navigate('/wallet');
        return;
      }
      if (session.verificationUrl) {
        const path = new URL(session.verificationUrl).pathname;
        void navigate(path);
      } else {
        await api.confirmAgeVerification();
        await loadStatus();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verification could not start.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section>
      <h1 style={{ fontFamily: 'var(--fm-font-heading)', marginTop: 0 }}>Age verification</h1>
      <p style={{ color: 'var(--fm-color-text-muted)' }}>
        FetMode requires age verification based on your region before adult features are available.
      </p>

      {isLoading && <p style={{ color: 'var(--fm-color-text-muted)' }}>Checking requirements…</p>}
      {error && <p className="fm-error">{error}</p>}

      {status && (
        <div className="fm-card">
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
            Your region: {status.jurisdictionCode} · Required: {status.requiredTier}
          </p>

          {status.action === 'attest' && (
            <>
              <label style={{ display: 'block', marginTop: '1rem' }}>
                Date of birth
                <input
                  type="date"
                  className="fm-input"
                  value={dateOfBirth}
                  onChange={(event) => {
                    setDateOfBirth(event.target.value);
                  }}
                />
              </label>
              <label style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(event) => {
                    setAcceptedTerms(event.target.checked);
                  }}
                />
                I confirm I am 18+ and accept the terms
              </label>
              <div style={{ marginTop: '1rem' }}>
                <Button
                  label={isSubmitting ? 'Submitting…' : 'Confirm age'}
                  disabled={!acceptedTerms || isSubmitting}
                  onClick={() => {
                    void handleAttest();
                  }}
                />
              </div>
            </>
          )}

          {status.action === 'verify' && (
            <>
              <p style={{ marginTop: '1rem' }}>
                Your state requires government ID verification before accessing adult content.
              </p>
              <div style={{ marginTop: '1rem' }}>
                <Button
                  label={isSubmitting ? 'Starting…' : 'Start ID verification'}
                  disabled={isSubmitting}
                  onClick={() => {
                    void handleStartVerification();
                  }}
                />
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--fm-color-text-muted)', marginTop: '0.75rem' }}>
                Dev uses a stub vendor. Production will plug in an adult-compliant verification provider.
              </p>
            </>
          )}

          {status.action === 'denied' && (
            <p className="fm-error" style={{ marginTop: '1rem' }}>
              FetMode is not available in your region.
            </p>
          )}
        </div>
      )}

      <p style={{ marginTop: '1rem' }}>
        <Link to="/wallet" style={{ color: 'var(--fm-color-text-muted)' }}>
          Back to wallet
        </Link>
      </p>
    </section>
  );
}
